import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Standardized */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Terms of Service</h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Terms governing access to and use of Enerlligence Analytics websites, platforms, and information services
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 pb-6 border-b border-slate-200">
            <p className="text-slate-600 text-sm">
              Enerlligence Analytics ("Company", "We", "Us", or "Our")
            </p>
            <p className="text-slate-500 text-sm mt-2">
              Effective Date: 1 January 2026 • Last Updated: 1 January 2026
            </p>
          </div>
          <div className="prose prose-lg max-w-none text-slate-700">
            
            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4 leading-relaxed">
              These Terms of Service ("Terms") govern access to and use of the websites, digital platforms, research portals, analytics tools, data products, publications, and related services (collectively, the "Services") provided by Enerlligence Analytics. By accessing or using the Services, users ("User", "Client", or "Subscriber") acknowledge that they have read, understood, and agreed to be bound by these Terms and all applicable laws and regulations. If you do not agree with these Terms, you must discontinue use of the Services.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">2. Nature of Services</h2>
            <p className="mb-4 leading-relaxed">
              The Company provides independent information, market intelligence, analytics, research insights, data services, and related professional content primarily covering energy, mining, infrastructure, and associated industries. All Services are provided for informational and professional use only, subject to the <Link to="/disclaimer" className="text-[#007BFF] hover:underline">Disclaimer</Link> and <Link to="/privacy-policy" className="text-[#007BFF] hover:underline">Privacy Policy</Link>.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">3. Eligibility and Authorised Use</h2>
            <p className="mb-4 leading-relaxed">Users represent and warrant that:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>They possess legal capacity to enter into binding agreements;</li>
              <li>Access to the Services complies with applicable laws in their jurisdiction;</li>
              <li>Information provided during registration is accurate and complete.</li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Access credentials issued to users are personal or institutional and may not be shared without authorisation.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">4. Account Registration and Security</h2>
            <p className="mb-4 leading-relaxed">Where account registration is required:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Users are responsible for maintaining confidentiality of login credentials;</li>
              <li>Users must promptly notify the Company of unauthorised access;</li>
              <li>The Company reserves the right to suspend accounts where misuse or security risks are identified.</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">5. Subscription and Service Access</h2>
            <p className="mb-4 leading-relaxed">
              Certain Services may be subject to subscription or contractual arrangements. Access rights:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>are limited, non-exclusive, and non-transferable;</li>
              <li>remain subject to payment compliance; and</li>
              <li>may be modified or updated periodically to improve service delivery.</li>
            </ul>
            <p className="mb-4 leading-relaxed">
              Failure to comply with subscription terms may result in suspension or termination of access.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">6. Intellectual Property Rights</h2>
            <p className="mb-4 leading-relaxed">
              All Content made available through the Services, including but not limited to: research reports, databases, analytics outputs, methodologies, software, graphics, trademarks, and branding, remain the exclusive intellectual property of Enerlligence Analytics or its licensors. Users may not reproduce, redistribute, commercially exploit, reverse engineer, or create derivative works without prior written consent.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">7. Permitted Use of Content</h2>
            <p className="mb-4 leading-relaxed">
              Users may utilise Content solely for internal business analysis, professional evaluation, research and decision-support purposes. Unauthorised resale, redistribution, or public dissemination is strictly prohibited.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">8. Data Usage and Platform Conduct</h2>
            <p className="mb-4 leading-relaxed">Users agree not to:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>misuse or manipulate platform data;</li>
              <li>attempt unauthorised system access;</li>
              <li>introduce malicious software;</li>
              <li>scrape, mine, or extract data using automated tools without permission;</li>
              <li>interfere with platform functionality or cybersecurity measures.</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">9. Confidentiality</h2>
            <p className="mb-4 leading-relaxed">
              Users acknowledge that certain materials provided through the Services may constitute proprietary or confidential information. Such information shall not be disclosed to third parties without written authorisation.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">10. Third-Party Content</h2>
            <p className="mb-4 leading-relaxed">
              The Services may include information sourced from third parties. The Company does not guarantee accuracy or completeness of third-party materials and assumes no responsibility for external content.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">11. Service Availability</h2>
            <p className="mb-4 leading-relaxed">
              While reasonable efforts are made to ensure uninterrupted access, the Company does not guarantee continuous availability of Services. Temporary interruptions may occur due to maintenance activities, cybersecurity measures, infrastructure upgrades, or events beyond reasonable control.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">12. Disclaimer of Warranties</h2>
            <p className="mb-4 leading-relaxed">
              Services and Content are provided on an "as available" and "as is" basis without warranties of any kind, whether express or implied. The Company does not guarantee accuracy, completeness, or suitability for specific commercial or investment purposes.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">13. Limitation of Liability</h2>
            <p className="mb-4 leading-relaxed">
              To the fullest extent permitted by law, the Company shall not be liable for any indirect or consequential damages, financial or commercial losses, loss of profits or business opportunities, or reliance-based losses arising from use of the Services.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">14. Suspension or Termination</h2>
            <p className="mb-4 leading-relaxed">The Company reserves the right to suspend or terminate access where:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Terms are breached;</li>
              <li>unlawful activity is suspected;</li>
              <li>payment obligations are unmet; or</li>
              <li>system security is threatened.</li>
            </ul>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">15. Compliance with Laws</h2>
            <p className="mb-4 leading-relaxed">
              Users agree to comply with all applicable laws, including export control, sanctions, and data protection regulations relevant to their jurisdiction.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">16. Amendments to Terms</h2>
            <p className="mb-4 leading-relaxed">
              The Company may revise these Terms periodically. Updated versions will be published on the Platform. Continued use of Services following updates constitutes acceptance of revised Terms.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">17. Governing Law and Jurisdiction</h2>
            <p className="mb-4 leading-relaxed">
              These Terms shall be governed by and construed in accordance with applicable laws, unless otherwise agreed under applicable client contracts.
            </p>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">18. Contact Information</h2>
            <p className="mb-4 leading-relaxed">For enquiries relating to these Terms:</p>
            <div className="bg-slate-50 p-6 rounded-lg border border-slate-200 mb-6">
              <p className="font-semibold text-[#1F3C88] mb-2">Legal & Compliance Department</p>
              <p className="text-slate-600">Enerlligence Analytics</p>
              <p className="text-slate-600">Email: <a href="mailto:legal@enerlligence.com" className="text-[#007BFF] hover:underline">legal@enerlligence.com</a></p>
              <p className="text-slate-600">Website: <a href="https://www.enerlligence.com" className="text-[#007BFF] hover:underline">www.enerlligence.com</a></p>
            </div>

            <h2 className="text-xl font-bold text-[#1F3C88] mt-8 mb-4">19. Corporate Governance Statement</h2>
            <p className="mb-4 leading-relaxed">
              The Board and Executive Management of Enerlligence Analytics maintain oversight of digital governance, information integrity, cybersecurity, and responsible dissemination of analytical content consistent with international information-service industry standards.
            </p>

            {/* Related Links */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="text-lg font-semibold text-[#1F3C88] mb-4">Related Policies</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/disclaimer" className="text-[#007BFF] hover:underline">Disclaimer</Link>
                <Link to="/privacy-policy" className="text-[#007BFF] hover:underline">Privacy Policy</Link>
                <Link to="/cookie-policy" className="text-[#007BFF] hover:underline">Cookie Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
